/**
 * 时间文本 → epoch 毫秒 —— **全内核唯一一份**。
 *
 * ## 为什么必须只有一份
 *
 * 时间常量（`const.datetime`）和 expr 的 `toEpoch` / `timePart` 都要解析时间文本。
 * 两家各写一份的话，「同一个字符串是什么意思」就会有两套答案 ——
 * 而这类错**不报错、只是算出来的日期差一天**，最难查。
 *
 * ## ⚠️ 这里在治的那个坑：ECMAScript 的日期解析有**两套**
 *
 * `Date.parse` 的行为按输入是否匹配一条**严格 ISO 正则**分岔
 * （`YYYY-MM-DD` 月日**必须补零**）：
 *
 * | 输入 | 走哪套 | 结果 |
 * |---|---|---|
 * | `2024-01-02` | 严格 ISO（日期形式） | **UTC** 午夜 |
 * | `2024-1-2` | 实现自定 | V8 当**本地时间**午夜 |
 * | `2024-01-02T00:00:00` | 严格 ISO（日期时间形式） | **本地时间**（规范如此） |
 * | `2024-1-2T08:00` | 实现自定 | V8 **解析失败** |
 *
 * 于是「同一个日期、只差补零」会落到两个不同的时刻：在 UTC+8，
 * `2024-1-2` 的本地午夜是 `2024-01-01T16:00Z`，下游取 UTC 的年月日
 * 就得到 **2024-01-01** —— 比用户输入早一天。它不报错，只是**打出来不对**。
 *
 * 这个坑是用户实测报回来的（2026-10 那一轮）：常量填 `2024-1-2`、
 * 经「格式化日期」资产出来 `2024-01-01`。**不能怪用户没补零** ——
 * 「按位置寻址」的那份稿子里同一条纪律：写法上的小事不该改变语义。
 *
 * ## 修法：把「松散 ISO」**归一化**成严格 ISO，再交给 `Date.parse`
 *
 * 只归一化**当年月日没补零**这一种情况（`2024-1-2` → `2024-01-02`），
 * 也就是**歧义最大的那一类**：补齐之后它和严格写法落到同一个时刻，
 * 不再依赖运行环境的时区。
 *
 * ⚠️ **刻意不做的三件事**（各自有理由，别顺手加）：
 *
 * 1. **不动带偏移的写法**（`2024-01-02T00:00:00+08:00`）。补零与否**不影响**
 *    它的含义，那是显式时刻，原样交给 `Date.parse`（V8 本来就认）。
 * 2. **不给纯日期补时间**。`2024-1-2` 归一化成 `2024-01-02`（UTC 午夜），
 *    和用户写全 `2024-01-02` 得到的**是同一个值** —— 这正是要的效果；
 *    替它补个 `T00:00:00`（本地时间）反而会把两套又分开。
 * 3. **不猜别的格式**（`2024/1/2`、`2 Jan 2024`、中文日期…）。
 *    那些没有「补零就变意思」的歧义，`Date.parse` 给什么就是什么；
 *    擅自扩大接受面会让「什么算合法输入」变得说不清。
 *
 * ## ⚠️ 归一化之后仍然存在的两个分岔（知道就好，不是 bug）
 *
 * 归一化只保证**补零与不补零结果一致**，不改变下面这两条**同一套内部的**差别：
 *
 * | 写法 | 归一化成 | `Date.parse` 怎么解 |
 * |---|---|---|
 * | `2024-1-2` / `2024-01-02`（**纯日期**） | `2024-01-02` | **UTC** 午夜 |
 * | `2024-1-2 08:00` / `2024-1-2T08:00`（**带时间**） | `2024-01-02 08:00` | **本地时间** |
 *
 * 也就是说：**纯日期按 UTC，带时间的按本地** —— 这是 `Date.parse` 自己的
 * 规矩（严格 ISO 的两种形式本来就分岔），我们只抹掉了「补零」这一个变量。
 * 想把「带时间的按本地」也改掉，那是**另一条决定**（会动到
 * `2024-01-02T00:00:00` 这个合法写法的既有含义），不在本模块的职责里。
 * 要让一个时刻不受运行环境影响，就写带偏移的（`...T08:00+08:00`）。
 */

/** 松散 ISO 的日期部分：月/日 1–2 位。**带偏移或纯日期时间的不在此列** */
const LOOSE_DATE = /^(\d{4})-(\d{1,2})-(\d{1,2})(?:(T| )(\d{1,2}):(\d{2})(?::(\d{2})(?:\.(\d{1,3}))?)?)?$/

/**
 * 把松散 ISO 日期补成严格 ISO；不是那种写法就**原样返回**。
 *
 * 判断只看**字符串形状**（有没有那个时区后缀），不看运行环境的时区 ——
 * 这是刻意的：解析结果不该随「谁在跑」而变。
 */
export function normalizeTimeText(text: string): string {
  const m = LOOSE_DATE.exec(text)
  if (!m) return text

  const [, y, mo, d, sep, h, mi, s, frac] = m
  const pad = (v: string) => v.padStart(2, '0')
  const date = `${y}-${pad(mo!)}-${pad(d!)}`
  // 纯日期：补齐就完了（严格 ISO 的日期形式 = UTC 午夜）
  if (!sep) return date
  // 日期 + 时间：时间部分也补齐，分隔符保留用户写的那个（T 或空格）
  const time = `${pad(h!)}:${mi}${s ? `:${pad(s)}` : ''}${frac ? `.${frac.padEnd(3, '0')}` : ''}`
  return `${date}${sep}${time}`
}

/**
 * 时间（epoch 毫秒 或 时间文本）→ epoch 毫秒。
 *
 * 解析不出来给 `null` —— 这里**不抛**（helper 的规矩），
 * 由调用方决定那是诊断还是一条空值。
 *
 * 接受的文本形状：
 *   · 纯数字文本（`'1704067200000'`）—— 当毫秒，和数字入参一致；
 *   · ISO / 松散 ISO 日期时间 —— 见 `normalizeTimeText`。
 */
export function parseTime(value: unknown): number | null {
  if (typeof value === 'number') return Number.isFinite(value) ? value : null
  if (typeof value !== 'string') return null

  const text = value.trim()
  if (text === '') return null

  const n = Number(text)
  if (Number.isFinite(n)) return n

  const ms = Date.parse(normalizeTimeText(text))
  return Number.isNaN(ms) ? null : ms
}
